import { type ReactElement, type ReactNode, useEffect, useRef } from 'react'

import type { StylableElementProps } from '../DefaultElementProps'
import { useDerivedState } from '../useDerivedState'
import { useLastDefined } from '../useLastDefined'
import { useScrollParentRef } from '../useScrollParentRef'
import type { BaseStep, GetExtendedStateRef } from '../useStepHandlers'
import { Container } from './Container'
import type { Options } from './Options'
import { Placeholder } from './Placeholder'
import { getRenderResult, type StepRenderers } from './StepRenderers'
import { useKeyGenerator } from './useKeyGenerator'
import { useTimers } from './useTimers'
import { type AnimationDirection, Wrapper } from './Wrapper'

export { stepWizardDefaultAnimations, stepWizardDefaultLoader, stepWizardDefaultOptions } from './defaultOptions'

export type { Options as StepWizardOptions, StepRenderers }

type MarginalProps<TStep extends BaseStep> = {
	lastStep?: TStep
	stepIndex?: number
	totalSteps: number
	isLoading: boolean
}

export type StepWizardProps<TStep extends BaseStep> = StylableElementProps & {
	header?: ReactNode | ((props: MarginalProps<TStep>) => ReactElement)
	footer?: ReactNode | ((props: MarginalProps<TStep>) => ReactElement)
	step?: TStep
	/** This is where all the steps are rendered conditionally.
	 * @example
	 * ```tsx
	 * <StepWizard>
	 * 	step={step}
	 * 	header={<Header />}
	 * 	renderers={{
	 * 		['First']: (currentStep) => (
	 * 			<StepOne {currentStep} />
	 * 		),
	 * 		['Second']: (currentStep) => ({
	 * 			content: <StepTwo {currentStep} />
	 * 			showHeader: false,
	 * 		}),
	 * 	}}
	 * />
	 * ```
	 */
	renderers: StepRenderers<TStep>
	getExtendedStateRef?: GetExtendedStateRef<TStep>
}

type StepInfo<TStep extends BaseStep> = {
	step: TStep
	key: string
	content: ReactNode
}

type State<TStep extends BaseStep> = {
	current?: StepInfo<TStep>
	previous?: StepInfo<TStep> & { height: number; offset: number }
	animatingOut: boolean
	animationDirection?: AnimationDirection
	showHeader: boolean
	showFooter: boolean
	loading: 'enter' | 'visible' | 'exit' | 'hide'
}

export const stepWizardCreate =
	({ animations, loader }: Options) =>
	<TStep extends BaseStep>({
		header,
		footer,
		step,
		renderers,
		getExtendedStateRef,
		className,
		style,
	}: StepWizardProps<TStep>) => {
		const currentWrapperRef = useRef<HTMLDivElement>(null)
		const getKey = useKeyGenerator()
		const timers = useTimers()
		const scrollParentRef = useScrollParentRef()

		const render = getRenderResult(step, renderers, getExtendedStateRef)

		const orderedTypes = Object.keys(renderers)

		const [{ current, previous, showHeader, showFooter, animatingOut, animationDirection, loading }, setState] =
			useDerivedState<State<TStep>>(
				() => ({
					current: step ? { key: getKey(step.type), step, content: render.content } : undefined,
					previous: undefined,
					loading: step ? 'hide' : 'enter',
					animatingOut: false,
					animationDirection: undefined,
					showHeader: render.showHeader ?? true,
					showFooter: render.showFooter ?? true,
				}),
				(state) => {
					if (step === undefined && state.current === undefined) {
						return state
					}

					// Same step as last, just update current element
					if (state.current && step === state.current.step) {
						return {
							...state,
							current: { ...state.current, content: render.content },
							showHeader: render.showHeader ?? true,
							showFooter: render.showFooter ?? true,
						}
					}

					let previous: State<TStep>['previous']
					if (!state.previous || state.previous.step !== step) {
						if (state.current) {
							previous = {
								...state.current,
								height: currentWrapperRef.current?.offsetHeight ?? 0,
								offset: currentWrapperRef.current?.offsetTop ?? 0,
							}
						} else {
							previous = state.previous
						}
					}

					const current = step
						? {
								// If stepping backwards, preserve key
								key: step === state.previous?.step ? state.previous.key : getKey(step.type),
								step,
								content: render.content,
							}
						: undefined

					return {
						current,
						previous,
						animationDirection:
							// Both current and previous, with current before previous
							(previous &&
								current &&
								previous !== state.previous &&
								orderedTypes.indexOf(previous.step.type) > orderedTypes.indexOf(current.step.type)) ||
							// Reverting to out-animating step
							(state.previous && state.previous.step === step && state.animationDirection === 'forward')
								? 'backward'
								: 'forward',
						animatingOut: state.animatingOut || Boolean(state.current),
						loading: current ? 'hide' : 'enter',
						showHeader: step ? (render.showHeader ?? true) : state.showHeader,
						showFooter: step ? (render.showFooter ?? true) : state.showFooter,
					}
				},
			)

		const animation = animationDirection ? animations[animationDirection] : undefined

		// If when page-change starts, wait a min time before animating next container in
		useEffect(() => {
			if (!animatingOut || !animation?.in.delay) return
			timers.set('animatingOut', animation.in.delay, () => setState((state) => ({ ...state, animatingOut: false })))
		}, [animatingOut, animation, setState, timers])

		// If current page switches from invisible to visible, set timer for clearing animation info
		useEffect(() => {
			if (!previous || !animation) return
			timers.set('previous', animation.out.duration, () => setState((state) => ({ ...state, previous: undefined })))
		}, [animation, previous, setState, timers])

		// When loader becomes invisible, set timer for when it should be removed completely
		useEffect(() => {
			switch (loading) {
				case 'exit':
					timers.set('loading', loader.duration, () => setState((state) => ({ ...state, loading: 'hide' })))
					break
				case 'enter':
					timers.set('loading', loader.delay ?? 0, () => setState((state) => ({ ...state, loading: 'visible' })))
					break
				default:
					timers.clear('loading')
					break
			}
		}, [loading, setState, timers])

		// Whenever the currently shown page changes, reset scroll
		useEffect(() => {
			scrollParentRef.current?.scrollTo(0, 0)
		}, [current?.key, scrollParentRef])

		// Disable scroll in parent when only previous is visible
		useEffect(() => {
			if (previous && !current) {
				return scrollParentRef.current?.disable()
			}
		}, [current, previous, scrollParentRef])

		const lastStep = useLastDefined(step, undefined)

		const renderMarginal = (marginal: StepWizardProps<TStep>['header' | 'footer']) => {
			if (typeof marginal !== 'function') return marginal

			const stepIndex = orderedTypes.indexOf(lastStep?.type ?? '')

			return marginal({
				lastStep,
				stepIndex: stepIndex >= 0 ? stepIndex : undefined,
				totalSteps: orderedTypes.length,
				isLoading: step === undefined,
			})
		}

		return (
			<Container ref={scrollParentRef} className={className} style={style}>
				{showHeader && renderMarginal(header)}
				{previous && !current && <Placeholder height={previous.height} />}
				{previous && (
					<Wrapper
						key={previous.key}
						phase='out'
						animation={animation}
						height={previous.height}
						offset={previous.offset}
					>
						{getRenderResult(previous.step, renderers).content}
					</Wrapper>
				)}
				{current && (
					<Wrapper ref={currentWrapperRef} key={current.key} phase='in' animation={animation}>
						{current.content}
					</Wrapper>
				)}
				{showFooter && renderMarginal(footer)}
				{loading !== 'hide' && <loader.Component isVisible={loading === 'visible'} />}
			</Container>
		)
	}
